<template>
  <div>
    <reports-title :title="pageTitle" />
    <reports-toolbar
      :drop-menu="menu"
      :report-title="pageTitle"
      :toolbar-disabled="isStatic || isDisabled"
      @onSelectDropdown="onChangeCalculationMode"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
    >
      <template #body>
        <bar-chart
          id="report_export_id"
          :config="{}"
          :stub-config="stubConfig"
          :is-static="isStatic"
          :is-disabled="true"
          :show-stub-message="false"
        />
      </template>
    </skeleton-wrapper>
    <bar-chart
      v-else
      id="report_export_id"
      :config="chartConfig"
      :stub-config="stubConfig"
      :is-static="isStatic"
      :is-disabled="isDisabled"
      :is-single-project="currentProjectIds.length === 1"
      :show-stub-message="true"
      :chart-name="chartName"
      :legend-items="currentProjectIds.length > 1 ? legendItems : []"
      @toggleChart="toggleData"
    />
  </div>
</template>

<script>
import multiViewsProjects from '$$multiviewModel';
import constants from '$$helpers/constants';
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import barChart from '../include/negativeBarChart.vue';
import Progress from './progress';
import charts from './chartsConfig';
import projectsModel from '$$projectModel';
import reportsConstants from '../include/constants';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';

export default {
  components: {
    reportsTitle, reportsToolbar, barChart, skeletonWrapper,
  },
  data() {
    return {
      locale: __,
      projectIds: [],
      multiviewProjectIds: [],
      baselines: [],
      baselineData: null,
      baselineId: null,
      chartConfig: {},
      stubConfig: {},
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      clientWidth: 0,
      isPositiveProgressDisabled: false,
      isNegativeProgressDisabled: false,
      isStatic: false,
      isDisabled: false,
      chartName: '',
      isShowSkeleton: true,
    };
  },
  computed: {
    pageTitle() {
      return reportsConstants.TITLE.progress.schedule;
    },
    menu() {
      return [
        {
          type: 'item',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}`),
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          isDisabled: this.isStatic,
          group: __('report_calculation_group_actual_label'),
          tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}_multiple_report`), position: 'right' },
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE ? this.baselineId : null,
          group: __('report_calculation_group_baseline_label'),
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}_multiple_report`), position: 'right' } : null,
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE ? this.baselineId : null,
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}_multiple_report`), position: 'right' } : null,
        },
      ];
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    legendItems() {
      return [
        {
          name: 'positive',
          label: __('report_progress_schedule_positive'),
          color: this.isPositiveProgressDisabled || this.isStatic ? '#E9E9E9' : '#8BC34A',
          disabled: this.isPositiveProgressDisabled || this.isStatic,
        },
        {
          name: 'negative',
          label: __('report_progress_schedule_negative'),
          color: this.isNegativeProgressDisabled || this.isStatic ? '#CCCCCC' : '#EE7950',
          disabled: this.isNegativeProgressDisabled || this.isStatic,
        },
      ];
    },
    filterProjectIds() {
      return [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];
    },
    currentProjectIds() {
      if (!this.filterProjectIds.length) {
        return projectsModel.getAllProjects().map(project => project.gantt_id);
      }

      return this.filterProjectIds;
    },
    sourceType() {
      if (this.multiviewProjectIds.length) {
        return 'multiview';
      }

      return 'projects';
    },
    isBaselineEnabled() {
      const baselines = [constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE, constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE];

      return baselines.includes(this.calculationMode);
    },
  },
  watch: {
    'currentFilter.properties.gantt_id': function (projectIds) {
      if (projectIds?.length) {
        this.projectIds = projectIds;
      } else {
        this.projectIds = [];
      }
    },
    'currentFilter.properties.portfolio_id': function (portfolioIds) {
      if (portfolioIds?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(portfolioIds[0]);

        this.multiviewProjectIds = multiviewData.ganttIDs;
      } else {
        this.multiviewProjectIds = [];
      }
    },
    async currentProjectIds(projectIds) {
      if (projectIds && projectIds.length) {
        if (projectIds.length > 1) {
          this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
        }
        await this.getBaselines();
        this.calcProgressReport();
      }
    },
    leftSidebarWidth(width) {
      this.clientWidth = `${document.documentElement.clientWidth - width}px`;
    },
    filterProjectIds(ids) {
      if (!ids.length) {
        this.isNegativeProgressDisabled = false;
        this.isPositiveProgressDisabled = false;
        this.isDisabled = false;
      }
    },
    calculationMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          calculationModel: newVal,
        },
      });
    },
    isBaselineEnabled(flag) {
      this.updateFilterAdvancedSettings({ baselineEnabled: flag });
    },
  },
  created() {
    this.chartConfig = charts.scheduleProgressChart.stub;
    this.stubConfig = charts.scheduleProgressChart.stub;
  },
  mounted() {
    this.clientWidth = `${document.documentElement.clientWidth - this.leftSidebarWidth}px`;
    this.init();

    userExtAnalytics.log('report_open', {
      report_type: this.$route?.params?.type,
      source_type: this.sourceType,
      projects_selected_count: this.currentProjectIds.length,
      calculation_mode: this.calculationMode,
    });
  },
  methods: {
    init() {
      this.calculationMode = this.$route?.query?.calculationModel || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;

      if (this.currentFilter?.properties?.gantt_id?.length) {
        this.projectIds = this.currentFilter.properties.gantt_id;
      }

      if (this.currentFilter?.properties?.portfolio_id?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(this.currentFilter?.properties?.portfolio_id[0]);

        if (multiviewData) {
          this.multiviewProjectIds = multiviewData.ganttIDs;
        }
      }

      if (!this.filterProjectIds.length) {
        this.projectIds = projectsModel.getAllProjects().map(project => project.gantt_id);
      }
    },

    updateFilterAdvancedSettings(settings) {
      if (this.currentFilter?.type) {
        this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
          type: this.currentFilter.type,
          settings,
        });
      }
    },

    async getBaselines() {
      this.baselineId = null;
      this.baselines = [];
      this.baselineData = {};

      this.updateFilterAdvancedSettings({ isShowNotificationMessage: false });
      if (this.currentProjectIds.length === 1) {
        await this.$store.dispatch('baselines/fetchBaselines', this.currentProjectIds[0]);

        const baselines = this.$store.getters['baselines/getProjectBaselines'](this.currentProjectIds[0])
          .map(item => ({ ...item, created_at: new Date(item.created_at) }))
          .sort((a, b) => a.created_at - b.created_at);

        if (baselines.length) {
          this.baselines = baselines;
          this.baselineId = baselines[baselines.length - 1].id;
          await this.getBaselineData(this.baselineId);
        } else {
          this.baselines = [];

          if (this.isBaselineEnabled) {
            this.updateFilterAdvancedSettings({ isShowNotificationMessage: true });
            this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
          }
        }
      }
    },
    async getBaselineData(id) {
      const baseline = this.baselines.find(b => b.id === id);

      if (!baseline) {
        this.baselineData = null;

        return;
      }

      const data = await this.$store.dispatch('baselines/getBaselineData', { projectId: baseline.gantt_id, eventId: baseline.event_id });

      if (data) {
        this.baselineData = data;
        this.baselineId = id;
      } else {
        this.baselineData = null;
      }
    },
    async calcProgressReport() {
      const result = await Progress.calculateScheduleReport({
        projectIds: this.currentProjectIds,
        calculationMode: this.calculationMode,
        baselineData: this.baselineData,
      });

      if (result) {
        this.chartConfig = { ...result.config };
        this.allData = result.config?.series[0]?.data;
        this.isStatic = false;

        if (this.currentProjectIds.length === 1) {
          const projectData = projectsModel.getProjectDataById(this.currentProjectIds[0]);

          this.chartName = projectData.name;
        } else {
          this.setDataBySelectedLegendItem();
          this.chartName = '';
        }
      } else {
        this.chartName = '';
        this.isStatic = true;
        this.isNegativeProgressDisabled = false;
        this.isPositiveProgressDisabled = false;
      }
      this.isShowSkeleton = false;
      setTimeout(() => statusHelper.handlers.hideMainSpinner(), 10);
    },
    async onChangeCalculationMode(selected) {
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;
      statusHelper.handlers.showMainSpinner();

      if (selected?.baselineId) {
        await this.getBaselineData(selected.baselineId);
      } else {
        this.baselineId = null;
      }

      this.calcProgressReport();
    },
    toggleData(name) {
      if (name === 'positive') {
        this.isPositiveProgressDisabled = !this.isPositiveProgressDisabled;
      }

      if (name === 'negative') {
        this.isNegativeProgressDisabled = !this.isNegativeProgressDisabled;
      }

      this.setDataBySelectedLegendItem();
    },
    setDataBySelectedLegendItem() {
      const filteredData = this.allData.filter(item => {
        if (!this.isPositiveProgressDisabled && item.value > 0) {
          return item;
        }

        if (!this.isNegativeProgressDisabled && item.value < 0) {
          return item;
        }
      });

      if (!filteredData.length) {
        this.isDisabled = true;

        return;
      }

      const filteredConfig = _.cloneDeep(this.chartConfig);

      filteredConfig.yAxis.data = filteredData.map(item => item.progressData.projectName);
      filteredConfig.series[0].data = filteredData;

      this.isDisabled = false;
      this.chartConfig = { ...filteredConfig };
    },
  },
};
</script>
